/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources } from '@msdyn365-commerce/components';
import { MilProductComponent } from './mil-product-component';
import { ICoreContext, IImageSettings } from '@msdyn365-commerce/core';
import { CommerceProperty, ProductSearchResult } from '@msdyn365-commerce/retail-proxy';
import { ArrayExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';
import { getMultipleStockStatusAsync, getMultiUnitOfMeasureConversionAsync } from '../../../actions/DataActionExtension.g';
import { IGetMultiUnitOfMeasureConversionResponse } from '../../../actions/DataServiceEntities.g';
import { IMilSearchResultContainerResources } from '../mil-search-result-container.props.autogenerated';

interface IProductSearchResultItems {
    products: ProductSearchResult[];
    context: ICoreContext;
    resources: IMilSearchResultContainerResources;
    imageSettings?: IImageSettings;
    moduleType: string;
    moduleId: string;
    allowBackNavigation?: boolean;
    telemetryContent: ITelemetryContent;
    quickviewSlot?: {} | null | undefined;
    productComparisonButton?: {} | null | undefined;
    channelInventoryConfigurationId?: number;
    isPriceMinMaxEnabled?: boolean;
    productsDimensionAvailabilities?: IProductsDimensionsAvailabilities[][];
    surchargeLabel: string;
    desiredNonstockCommerceName?: string;
    nonstockCommerceState?: string;
}

/**
 * Returns the product inventory label.
 * @param  channelInventoryConfigurationId - The channel configuration Id.
 * @param  product - The product.
 * @returns The inventory label.
 */
export function getInventoryLabel(channelInventoryConfigurationId: number | undefined, product: ProductSearchResult): string | undefined {
    if (!channelInventoryConfigurationId || !ArrayExtensions.hasElements(product.AttributeValues)) {
        return undefined;
    }
    const inventoryAttribute = product.AttributeValues.find(attribute => attribute.RecordId === channelInventoryConfigurationId);
    if (inventoryAttribute) {
        return inventoryAttribute.TextValue;
    }
    return undefined;
}

export const ProductSearchResultItems: React.FC<IProductSearchResultItems> = ({
    products,
    context,
    imageSettings,
    resources,
    moduleType,
    moduleId,
    allowBackNavigation,
    telemetryContent,
    quickviewSlot,
    productComparisonButton,
    channelInventoryConfigurationId,
    isPriceMinMaxEnabled,
    productsDimensionAvailabilities,
    surchargeLabel,
    desiredNonstockCommerceName,
    nonstockCommerceState
}) => {
    const priceResources: IPriceComponentResources = {
        priceRangeSeparator: resources.priceRangeSeparator
    };

    const [uomConvData, setUomConvData] = React.useState<IGetMultiUnitOfMeasureConversionResponse | undefined>();

    React.useEffect(() => {
        void getUomConversionData(products);
    }, [products]);

    const getUomConversionData = async (items: ProductSearchResult[]) => {
        const itemIds: number[] = [];
        items.map(item => {
            if (item.RecordId) {
                itemIds.push(item.RecordId);
            }
        });
        const stockStatuses = await getMultipleStockStatusAsync({ callerContext: context.actionContext }, itemIds);

        const resp: IGetMultiUnitOfMeasureConversionResponse = await getMultiUnitOfMeasureConversionAsync(
            { callerContext: context.actionContext },
            itemIds
        );
        if (resp && resp.UomConvMap && resp.UomConvMap.length > 0) {
            const tempRespUomData = [...resp.UomConvMap];
            items.map(item => {
                let index = -1;
                const uomData = tempRespUomData.find((res, i) => {
                    if (res.ItemRecId === item.RecordId) {
                        index = i;
                        return res;
                    }
                    return;
                });

                let stockDataIdx = -1;
                const stockStatusData = stockStatuses.StockStatusMap?.find((res, i) => {
                    if (res.ItemRecId === item.RecordId) {
                        stockDataIdx = i;
                        return res;
                    }
                    return;
                });

                if (index >= 0 && uomData) {
                    tempRespUomData.splice(index, 1);
                    const priceUnitProp: CommerceProperty = {
                        Key: 'uomPriceUnit',
                        Value: { DecimalValue: uomData.EcommPriceUnit || 1 }
                    };
                    const uomDescProp: CommerceProperty = {
                        Key: 'uomDesc',
                        Value: { StringValue: uomData.DefaultDescription }
                    };
                    const surchargeAmountProp: CommerceProperty = {
                        Key: 'surchargeAmount',
                        Value: { DecimalValue: uomData.EcommSurchargeAmount || 0 }
                    };
                    const surchargeFactorProp: CommerceProperty = {
                        Key: 'surchargeFactor',
                        Value: { DecimalValue: uomData.EcommSurchargeFactor || 0 }
                    };
                    const isSurchargePercentProp: CommerceProperty = {
                        Key: 'isSurchargePercent',
                        Value: { BooleanValue: uomData.IsEcommSurchargePercent || false }
                    };
                    const surchargeLabelProp: CommerceProperty = {
                        Key: 'surchargeLabel',
                        Value: { StringValue: surchargeLabel || 'Surcharge incl.' }
                    };
                    if (item.ExtensionProperties) {
                        item.ExtensionProperties.push(priceUnitProp);
                        item.ExtensionProperties.push(uomDescProp);
                        item.ExtensionProperties.push(surchargeAmountProp);
                        item.ExtensionProperties.push(surchargeFactorProp);
                        item.ExtensionProperties.push(isSurchargePercentProp);
                        item.ExtensionProperties.push(surchargeLabelProp);
                    }
                }

                if (stockDataIdx >= 0 && stockStatusData) {
                    const stockStatusProp: CommerceProperty = {
                        Key: 'stockStatus',
                        Value: { StringValue: stockStatusData.StockStatus }
                    };
                    if (item.ExtensionProperties) {
                        item.ExtensionProperties.push(stockStatusProp);
                    }
                }
            });
            setUomConvData(resp);
        }
    };

    return (
        <ul className='list-unstyled'>
            {!!uomConvData &&
                products.map((product: ProductSearchResult, index: number) => (
                    <li className='ms-product-search-result__item' key={index}>
                        <MilProductComponent
                            context={context}
                            telemetryContent={telemetryContent}
                            imageSettings={imageSettings}
                            freePriceText={resources.priceFree}
                            originalPriceText={resources.originalPriceText}
                            currentPriceText={resources.currentPriceText}
                            shouldDisplayPrice={!!uomConvData}
                            ratingAriaLabel={resources.ratingAriaLabel}
                            allowBack={allowBackNavigation}
                            id={moduleId}
                            key={product.RecordId}
                            typeName={moduleType}
                            data={{ product }}
                            quickViewButton={quickviewSlot}
                            productComparisonButton={productComparisonButton}
                            inventoryLabel={getInventoryLabel(channelInventoryConfigurationId, product)}
                            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
                            priceResources={priceResources}
                            swatchItemAriaLabel={resources.swatchItemAriaLabel}
                            dimensionAvailabilities={productsDimensionAvailabilities?.find(
                                dimensionAvailability =>
                                    ArrayExtensions.hasElements(dimensionAvailability) &&
                                    dimensionAvailability[0].masterProductId === product.MasterProductId
                            )}
                            desiredNonstockCommerceName={desiredNonstockCommerceName || ''}
                            nonstockCommerceState={nonstockCommerceState || ''}
                            stockStatusText={resources.stockStatusText}
                            // productStockStatus={}
                        />
                    </li>
                ))}
        </ul>
    );
};
